import type { SchemaType } from '@based/schema'
import { seoMetaDescription, seoMetaTitle } from './pageSeo'
import { SchemaToType } from './utils'

export const PLOT_APPLICATION_STATUSES = {
  applicationOpen: 'applicationOpen',
  applicationClosed: 'applicationClosed',
  inPreparation: 'inPreparation',
} as const
export type PlotApplicationStatus = keyof typeof PLOT_APPLICATION_STATUSES

export const PLOT_AVAILABILITY = {
  draft: 'draft',
  active: 'active',
  assigned: 'assigned',
} as const
export type PlotAvailability = keyof typeof PLOT_AVAILABILITY

export const plot = {
  uuid: { type: 'alias', format: 'UUID' },
  slug: { type: 'alias' },
  name: 'string',
  description: 'string',
  metaTitle: seoMetaTitle,
  metaDescription: seoMetaDescription,
  infoTextBox: 'string',
  availability: Object.values(PLOT_AVAILABILITY),
  byCrowdbuilding: 'boolean',
  applicationStatus: Object.values(PLOT_APPLICATION_STATUSES),
  contentBlocks: {
    items: {
      ref: 'pageContentBlock',
      prop: 'plot',
      $position: [
        'overviewTabTop',
        'overviewTabBottom',
        'applicationTab',
        'documentsTab',
      ],
    },
  },
  media: { items: { ref: 'file', prop: 'plotMedia' } },
  location: {
    props: {
      geo: {
        props: {
          lat: 'number',
          long: 'number',
        },
      },
      address: {
        props: {
          country: { ref: 'country', prop: 'plots' },
          region: { ref: 'region', prop: 'plots' },
          municipality: { ref: 'municipality', prop: 'plots' },
          city: 'string',
          street: 'string',
          postalCode: 'string',
        },
      },
    },
  },
  createdAt: { type: 'timestamp', on: 'create' },
  updatedAt: { type: 'timestamp', on: 'update' },
  publishedAt: 'timestamp',
  applicationDeadline: 'timestamp',
  owner: { ref: 'plotOwner', prop: 'plots' },
  createdBy: { ref: 'user', prop: 'createdPlots' },
  followers: { items: { ref: 'user', prop: 'plots' } },
  followingInitiatives: {
    items: { ref: 'initiative', prop: 'followingPlots' },
  },
  applications: { items: { ref: 'plotApplication', prop: 'plot' } },
  selectedInitiative: { ref: 'initiative', prop: 'plot' },
  housingForms: { items: { ref: 'housingForm', prop: 'plots' } },
  thread: { ref: 'thread', prop: 'plot' },
  plotRegistry: { ref: 'plotRegistry', prop: 'plot' },
  spotlights: { items: { ref: 'spotlight', prop: 'plot' } },
  // Feature fields
  numberOfUnits: 'uint16',
  minNumberOfUnits: 'uint16',
  maxNumberOfUnits: 'uint16',
  propertyType: 'string',
  offerType: { ref: 'offerType', prop: 'plots' },
  buildingType: { ref: 'buildingType', prop: 'plots' },
  developmentForm: { ref: 'developmentForm', prop: 'plots' },
  ownershipSituation: { ref: 'ownershipSituation', prop: 'plots' },
  livingEnvironment: { ref: 'livingEnvironment', prop: 'plots' },
  typeOfOrganisation: 'string',
  organisationName: 'string',
  // Contact fields
  contactName: 'string',
  contactEmail: 'string',
  contactPhoneNumber: 'string',
  contactWebsite: 'string',
} satisfies SchemaType

export type Plot = SchemaToType<typeof plot> & {
  id: number
}
// TODO: See if CrowdBuildingSchema works fine
// and use that instead of SchemaToType if so
//export type Plot = CrowdBuildingSchema['plot']
